import * as Yup from 'yup';
import { EPM_LIMITS, EPM_PATTERNS, isValidRegexString } from './epmRules';

/**
 * Testing Board: New Idea Modal Schema
 * Returns a Yup schema parameterized with existingTests to enforce case-insensitive uniqueness.
 */
export const getNewIdeaSchema = (existingTests = []) =>
    Yup.object().shape({
        key: Yup.string()
            .trim()
            .max(EPM_LIMITS.TEST_KEY_MAX, `Keep the ID to ${EPM_LIMITS.TEST_KEY_MAX} characters`)
            .test(
                'valid-pattern',
                'Test ID must start with a letter/number and contain only letters, numbers, spaces, dots, underscores, slashes, or hyphens.',
                (val) => !val || EPM_PATTERNS.TEST_KEY.test(val)
            )
            .test(
                'unique-key',
                'This Test ID is already in use in your organization.',
                (val) => {
                    if (!val) return true;
                    const normalized = val.trim().toLowerCase();
                    return !(existingTests || []).some(
                        (t) => String(t.key || '').trim().toLowerCase() === normalized
                    );
                }
            ),
        name: Yup.string()
            .trim()
            .min(EPM_LIMITS.IDEA_TITLE_MIN, `Idea title must be at least ${EPM_LIMITS.IDEA_TITLE_MIN} characters.`)
            .max(EPM_LIMITS.IDEA_TITLE_MAX, `Idea title must be ${EPM_LIMITS.IDEA_TITLE_MAX} characters or fewer.`)
            .required('Idea title is required'),
        description: Yup.string()
            .max(EPM_LIMITS.IDEA_DESC_MAX, `Description must be ${EPM_LIMITS.IDEA_DESC_MAX} characters or fewer.`),
    });

/**
 * Step 1: Idea Schemas
 */
export const hypothesisSchema = Yup.string()
    .trim()
    .min(EPM_LIMITS.HYPOTHESIS_MIN, `Hypothesis must be at least ${EPM_LIMITS.HYPOTHESIS_MIN} characters.`)
    .max(EPM_LIMITS.HYPOTHESIS_MAX, `Hypothesis must be ${EPM_LIMITS.HYPOTHESIS_MAX} characters or fewer.`)
    .required('Hypothesis is required to move on.');

export const hypothesisFormatSchema = Yup.string()
    .max(EPM_LIMITS.HYPOTHESIS_MAX, `Hypothesis must be ${EPM_LIMITS.HYPOTHESIS_MAX} characters or fewer.`);

export const evidenceItemSchema = Yup.string()
    .trim()
    .max(EPM_LIMITS.EVIDENCE_ITEM_MAX, `Evidence item must be ${EPM_LIMITS.EVIDENCE_ITEM_MAX} characters or fewer.`)
    .test(
        'url-pattern',
        "Items starting with 'http' must be valid URLs (e.g. https://analytics.company.com).",
        (val) => !val || !val.toLowerCase().startsWith('http') || EPM_PATTERNS.HTTP_URL.test(val)
    );

/**
 * Step 2: Priority Schemas
 */
export const iceScoreSchema = Yup.number()
    .integer('Score must be a whole number.')
    .min(EPM_LIMITS.SCORE_MIN, `Score must be at least ${EPM_LIMITS.SCORE_MIN}.`)
    .max(EPM_LIMITS.SCORE_MAX, `Score must be at most ${EPM_LIMITS.SCORE_MAX}.`)
    .required('Score is required.');

export const decisionReasonSchema = Yup.string()
    .trim()
    .min(EPM_LIMITS.DECISION_REASON_MIN, `Decision reason must be at least ${EPM_LIMITS.DECISION_REASON_MIN} characters.`)
    .max(EPM_LIMITS.DECISION_REASON_MAX, `Decision reason must be ${EPM_LIMITS.DECISION_REASON_MAX} characters or fewer.`)
    .required('Decision reason is required.');

export const decisionReasonFormatSchema = Yup.string()
    .max(EPM_LIMITS.DECISION_REASON_MAX, `Decision reason must be ${EPM_LIMITS.DECISION_REASON_MAX} characters or fewer.`);

/**
 * Step 3: Plan Schemas
 */
export const primaryMetricSchema = Yup.string()
    .trim()
    .min(EPM_LIMITS.PRIMARY_METRIC_MIN, `Primary metric must be at least ${EPM_LIMITS.PRIMARY_METRIC_MIN} characters.`)
    .max(EPM_LIMITS.PRIMARY_METRIC_MAX, `Primary metric must be ${EPM_LIMITS.PRIMARY_METRIC_MAX} characters or fewer.`)
    .required('Primary metric is required to move on.');

export const primaryMetricFormatSchema = Yup.string()
    .max(EPM_LIMITS.PRIMARY_METRIC_MAX, `Primary metric must be ${EPM_LIMITS.PRIMARY_METRIC_MAX} characters or fewer.`);

export const secondaryMetricItemSchema = Yup.string()
    .trim()
    .min(1, 'Secondary metric cannot be empty.')
    .max(EPM_LIMITS.SECONDARY_METRIC_MAX, `Secondary metric must be ${EPM_LIMITS.SECONDARY_METRIC_MAX} characters or fewer.`)
    .required('Secondary metric is required.');

/**
 * Step 4: Build & QA Schemas
 */
export const variantDetailsSchema = Yup.string()
    .trim()
    .max(EPM_LIMITS.VARIANT_DETAILS_MAX, `Details must be ${EPM_LIMITS.VARIANT_DETAILS_MAX} characters or fewer.`)
    .required('Details are required.');

export const oneOffChecklistSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .min(EPM_LIMITS.ONE_OFF_CHECKLIST_NAME_MIN, `Checklist item name must be at least ${EPM_LIMITS.ONE_OFF_CHECKLIST_NAME_MIN} character.`)
        .max(EPM_LIMITS.ONE_OFF_CHECKLIST_NAME_MAX, `Checklist item name must be ${EPM_LIMITS.ONE_OFF_CHECKLIST_NAME_MAX} characters or fewer.`)
        .required('Checklist item name is required.'),
    description: Yup.string()
        .max(EPM_LIMITS.ONE_OFF_CHECKLIST_DESC_MAX, `Description must be ${EPM_LIMITS.ONE_OFF_CHECKLIST_DESC_MAX} characters or fewer.`),
});

/**
 * Step 6: Results Schemas
 */
export const verdictStatusSchema = Yup.string()
    .oneOf(['win', 'loss', 'inconclusive', 'invalid'], 'Verdict must be Win, Loss, Inconclusive, or Invalid.')
    .required('Verdict is required.');

export const rationaleSchema = Yup.string()
    .trim()
    .min(EPM_LIMITS.RATIONALE_MIN, `Rationale must be at least ${EPM_LIMITS.RATIONALE_MIN} characters.`)
    .max(EPM_LIMITS.RATIONALE_MAX, `Rationale must be ${EPM_LIMITS.RATIONALE_MAX} characters or fewer.`)
    .required('Rationale is required.');

export const rationaleFormatSchema = Yup.string()
    .max(EPM_LIMITS.RATIONALE_MAX, `Rationale must be ${EPM_LIMITS.RATIONALE_MAX} characters or fewer.`);

export const learningAssumptionSchema = Yup.string()
    .trim()
    .min(EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN, `'What we now believe' must be at least ${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN} characters.`)
    .max(EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX, `'What we now believe' must be ${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX} characters or fewer.`)
    .required('Learning belief is required to record learning.');

export const learningAssumptionFormatSchema = Yup.string()
    .max(EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX, `'What we now believe' must be ${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX} characters or fewer.`);

export const resultLinkSchema = Yup.string()
    .trim()
    .matches(EPM_PATTERNS.RESULT_LINK, 'Must be a valid http(s) URL.')
    .max(EPM_LIMITS.RESULT_LINK_MAX, `Result link must be ${EPM_LIMITS.RESULT_LINK_MAX} characters or fewer.`);

export const resultLinkFormatSchema = Yup.string()
    .trim()
    .max(EPM_LIMITS.RESULT_LINK_MAX, `Result link must be ${EPM_LIMITS.RESULT_LINK_MAX} characters or fewer.`)
    .test(
        'valid-url',
        'Must be a valid http(s) URL.',
        (val) => !val || EPM_PATTERNS.RESULT_LINK.test(val)
    );

/**
 * Settings Schemas
 */
export const tagSchema = Yup.string()
    .trim()
    .lowercase()
    .matches(EPM_PATTERNS.TAG_NAME, "Tag must be lowercase letters, numbers, hyphens or slashes (e.g. 'cta-copy', 'a/b').")
    .max(EPM_LIMITS.TAG_NAME_MAX, `Tag must be ${EPM_LIMITS.TAG_NAME_MAX} characters or fewer.`)
    .required('Tag name is required.');

export const locationFieldSchema = Yup.object().shape({
    label: Yup.string()
        .trim()
        .max(EPM_LIMITS.LOCATION_LABEL_MAX, `Label must be ${EPM_LIMITS.LOCATION_LABEL_MAX} characters or fewer.`)
        .required('Label is required.'),
    key: Yup.string()
        .trim()
        .max(EPM_LIMITS.LOCATION_KEY_MAX, `Key must be ${EPM_LIMITS.LOCATION_KEY_MAX} characters or fewer.`)
        .matches(EPM_PATTERNS.LOCATION_KEY, 'Key must start with a lowercase letter and contain only lowercase letters, numbers, and underscores (max 40 chars).')
        .required('Key is required.'),
    validation_pattern: Yup.string()
        .test('is-valid-regex', 'Validation pattern must be a valid regular expression.', (val) => {
            if (!val || !val.trim()) return true;
            return isValidRegexString(val.trim());
        }),
});

/**
 * Creates a reusable validator function from a Yup schema for format checks.
 * Returns null if valid, or the error message string if invalid.
 */
export const createValidator = (schema) => (value) => {
    try {
        schema.validateSync(value);
        return null;
    } catch (err) {
        return err.message;
    }
};

